/* HighestImpactConversations.jsx — P3 · Command Centre Hero.
 *
 * Replaces the legacy `NeedsAttentionSection` in-place as the
 * Command Centre's conversation-first hero.  Answers one
 * question:  "Who should I contact first to move the most
 * transactions today?"
 *
 * SSOT · zero new endpoints · zero new state · zero new
 * workflow: every field is derived client-side from the same
 * `needs` items + deals + contacts + participants that the
 * legacy section already consumed.  When `?legacy=1` is on the
 * URL, the parent falls back to the old section so pilots can
 * roll back instantly.
 *
 * Ranking (deterministic, transparent, no AI):
 *   1. Transactions affected (desc) — highest-impact contact wins.
 *   2. Oldest unresolved blocker (desc) — break ties on delay.
 *   3. Nearest exchange target across affected deals (asc).
 *   4. Highest cumulative fee exposure (desc).
 *   5. Longest time since last meaningful communication (desc).
 *
 * Filters (all client-side, apply after ranking):
 *   • Role tabs   — All / Solicitors / Buyers / Sellers /
 *                   Managing Agents / Brokers.
 *   • More filters — Role · Status · Transactions affected ·
 *                   Exchange target (7/14/30d) · Overdue only ·
 *                   Assigned to me.
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { mailtoHref, telHref, whatsappHref } from "../../lib/contactLinks";
import {
  Phone, Mail, MessageCircle, Send, MoreHorizontal, X,
  Filter, ChevronRight, ChevronDown, Flame, ArrowUp, ArrowDown, Minus, Plus,
  Clock, CheckCircle2, AlertTriangle, AlertCircle, Users, Building2,
  Sparkles, ArrowRightLeft,
} from "lucide-react";


/* ═══ Role vocabulary — matches SSOT participant_type values ═══ */
const OWNER_ROLE_LABEL = {
  buyer:               "Buyer",
  seller:              "Seller",
  buyer_solicitor:     "Buyer Solicitor",
  seller_solicitor:    "Seller Solicitor",
  // Canonical backend slug variants (see /app/backend/blocker_engine.py).
  // Kept as aliases so no slug ever renders as a raw "solicitor_seller"
  // subtitle on the Command Centre priorities row.
  solicitor_buyer:     "Buyer Solicitor",
  solicitor_seller:    "Seller Solicitor",
  mortgage_broker:     "Mortgage Broker",
  // iter-alignment · Agent Drawer Pilot Closure (2026-02) — mirror
  // the backend `_OWNER_ROLE_TO_PARTICIPANT` map
  // (/app/backend/blocker_engine.py:676).  Blockers with
  // `owner_role="agent"` (e.g. Deal Marked At Risk) target the
  // deal's estate agent via `deal.agent_id`; `owner_role="broker"`
  // is an alias for `mortgage_broker` via `deal.broker_id`.
  agent:               "Estate Agent",
  broker:              "Mortgage Broker",
  managing_agent:      "Managing Agent",
  search_provider:     "Search Provider",
  surveyor:            "Surveyor",
  lender:              "Lender",
};

/* Filter tab → predicate over the aggregated contact row. */
const ROLE_TABS = [
  { id: "all",             label: "All",              roles: null },
  // iter-alignment · QA sweep (2026-08-23) — the backend blocker
  // envelope emits the canonical slugs `solicitor_buyer` /
  // `solicitor_seller` (see blocker_engine.py).  The tab predicates
  // only listed the `*_solicitor` spellings, so every solicitor tab
  // count rendered 0 and the tab showed the empty state even though
  // the "All" queue was full of solicitor rows.  Both spellings are
  // now accepted (same aliases as _ROLE_TO_PARTICIPANT_TYPES).
  { id: "solicitors",      label: "Solicitors",       roles: ["buyer_solicitor", "seller_solicitor", "solicitor_buyer", "solicitor_seller"] },
  { id: "buyers",          label: "Buyers",           roles: ["buyer"] },
  { id: "sellers",         label: "Sellers",          roles: ["seller"] },
  { id: "managing_agents", label: "Managing Agents",  roles: ["managing_agent"] },
  { id: "brokers",         label: "Brokers",          roles: ["mortgage_broker"] },
];

/* Momentum-score direction glyph (visual hint only). */
const MOMENTUM_GLYPH = {
  hot:     { icon: Flame,   className: "bg-rose-50 text-rose-600" },
  rising:  { icon: ArrowUp, className: "bg-emerald-50 text-emerald-600" },
  falling: { icon: ArrowDown, className: "bg-rose-50 text-rose-600" },
  steady:  { icon: Minus,   className: "bg-slate-100 text-slate-500" },
  new:     { icon: Plus,    className: "bg-emerald-50 text-emerald-600" },
};

/* ═══ Deterministic ranking + aggregation ══════════════════════ */
/* iter-alignment · P0 · Command Centre Data Reconciliation (2026-02-28)
 * Owner-role → canonical `participant_type` alias map.  The pilot
 * seed populates `deal_participants` (typed rows fanned out on
 * `deal.participants`) but leaves the top-level FK columns empty,
 * so the previous FK-only resolver was returning null on every
 * deal → every blocker collapsed into ONE synthetic role bucket
 * (Priority-count mismatch, missing Buyer/Seller).  We now fall
 * back to the same canonical `deal_participants` slice the Deal
 * Summary Modal reads (P0 · Participant SSOT Fanout · 2026-02-28). */
const _ROLE_TO_PARTICIPANT_TYPES = {
  buyer:            ["buyer"],
  seller:           ["seller"],
  buyer_solicitor:  ["buyer_solicitor"],
  seller_solicitor: ["seller_solicitor"],
  solicitor_buyer:  ["buyer_solicitor"],
  solicitor_seller: ["seller_solicitor"],
  mortgage_broker:  ["mortgage_broker"],
  broker:           ["mortgage_broker", "broker"],
  managing_agent:   ["managing_agent"],
  // iter-alignment · Agent Drawer Pilot Closure (2026-02) — mirror
  // backend `_OWNER_ROLE_TO_PARTICIPANT["agent"] = ("agent",
  // "estate_agent", "agent_id")`.  Without this, blockers with
  // `owner_role="agent"` (Deal Marked At Risk) never resolved a
  // participant and every row collapsed into a synthetic bucket.
  agent:            ["agent", "estate_agent", "negotiator"],
};

export function aggregateContacts({ items, deals, contacts, feeByDeal, tExByDeal, currentUserId }) {
  const findContact = (id) => id ? contacts.find((c) => c.id === id) : null;
  const dealsById   = new Map(deals.map((d) => [d.id, d]));

  // deal + owner_role → contact_id lookup.  Falls back to the typed
  // `deal.participants` slice when the top-level FK is missing.
  const resolveContact = (deal, owner_role) => {
    if (!deal || !owner_role) return null;
    const fkMap = {
      buyer:            deal.buyerId,
      seller:           deal.sellerId,
      buyer_solicitor:  deal.buyerSolicitorId,
      seller_solicitor: deal.sellerSolicitorId,
      // Backend canonical slug variants — resolve the same contact
      // as their `_solicitor` counterparts so the priority row
      // renders a real person (not a synthetic bucket).
      solicitor_buyer:  deal.buyerSolicitorId,
      solicitor_seller: deal.sellerSolicitorId,
      mortgage_broker:  deal.brokerId,
      broker:           deal.brokerId,
      managing_agent:   deal.managingAgentId,
      // iter-alignment · Agent Drawer Pilot Closure (2026-02) —
      // `deal.agent_id` is populated by seed_demo_deal_links.py +
      // every real deal write path (routes_deals.py).  Backend
      // `_OWNER_ROLE_TO_PARTICIPANT["agent"]` already treats it as
      // the canonical FK for `owner_role="agent"` blockers.
      agent:            deal.agentId,
    };
    const viaFk = findContact(fkMap[owner_role]);
    if (viaFk) return viaFk;
    // Participant SSOT fallback — same slice the Deal Summary Modal
    // uses.  Never mutates state; produces a Contact-shaped object
    // so downstream row rendering stays identical.
    const types = _ROLE_TO_PARTICIPANT_TYPES[owner_role] || [];
    const parts = Array.isArray(deal.participants) ? deal.participants : [];
    const match = parts.find((p) => types.includes(p.participant_type));
    if (match) {
      // Prefer the canonical Contact row if we can resolve one via
      // the participant's contact_id (retains phone / email etc.);
      // otherwise fall through to a lightweight display object.
      const c = findContact(match.contact_id);
      if (c) return c;
      return {
        id: match.contact_id || `participant:${owner_role}:${deal.id}`,
        name: match.full_name || OWNER_ROLE_LABEL[owner_role] || owner_role,
        full_name: match.full_name,
        email: match.email || null,
        phone: match.phone || null,
        company: match.organisation_name || "",
        role: OWNER_ROLE_LABEL[owner_role] || owner_role,
      };
    }
    return null;
  };

  // Group items by (contact_id | fallback: role · deal-owner-name).
  const bucket = new Map();
  for (const item of items) {
    const deal = dealsById.get(item.deal_id);
    if (!deal) continue;
    const role = item.blocker.owner_role;
    const contact = resolveContact(deal, role);
    // If no Postgres Contact resolves for this owner_role, fall
    // back to a synthetic role-scoped bucket so the row still
    // surfaces (chainStore contacts can lag /api/today on cold
    // load; role + deal-owner is enough to drive the ranking).
    const bucketKey = contact
      ? contact.id
      : `role:${role || "unknown"}`;
    const bucketContact = contact || {
      id: bucketKey,
      name: OWNER_ROLE_LABEL[role] || (role ? role.replace(/_/g, " ") : "Unassigned"),
      company: "—",
      role: OWNER_ROLE_LABEL[role] || role || "",
      _synthetic: true,
    };
    if (!bucket.has(bucketKey)) {
      bucket.set(bucketKey, {
        contact: bucketContact,
        role,
        items: [],
        deal_ids: new Set(),
        oldest_delay_days: 0,
        potential_fee: 0,
        exchanges_this_week: 0,
        nearest_exchange_iso: null,
        last_comm_iso: null,
        primary_blocker: null,
        assigned_ids: new Set(),  // for "Assigned to me" filter
      });
    }
    const entry = bucket.get(bucketKey);
    entry.items.push(item);
    entry.deal_ids.add(item.deal_id);
    entry.potential_fee += (feeByDeal?.[item.deal_id] || 0);
    // iter-alignment · P0 · Command Centre Data Reconciliation
    // (2026-02-28) — the backend blocker envelope emits `age_days`
    // (see `/api/today/sheet` → routes_today.py::_shape_blocker).
    // The previous `days_open` read never resolved → oldest_delay
    // was pinned at 0, ranking collapsed and "Today overdue"
    // rendered on every row.  Read the canonical field and keep
    // `days_open` as a safety alias for any legacy caller.
    const ageDays = Number(item.blocker.age_days ?? item.blocker.days_open ?? 0) || 0;
    entry.oldest_delay_days = Math.max(entry.oldest_delay_days, ageDays);
    if (deal.agentId) entry.assigned_ids.add(deal.agentId);
    // Exchange-this-week + nearest
    const tex = tExByDeal?.[item.deal_id];
    if (tex) {
      const daysUntil = Math.floor((new Date(tex) - Date.now()) / 86400000);
      if (daysUntil >= 0 && daysUntil <= 7) entry.exchanges_this_week += 1;
      if (!entry.nearest_exchange_iso || new Date(tex) < new Date(entry.nearest_exchange_iso)) {
        entry.nearest_exchange_iso = tex;
      }
    }
    // Primary blocker = highest age_days of all items on this contact.
    const primaryAge = Number(entry.primary_blocker?.age_days ?? entry.primary_blocker?.days_open ?? 0) || 0;
    if (!entry.primary_blocker || ageDays > primaryAge) {
      entry.primary_blocker = item.blocker;
    }
    // last_comm — reuse blocker.last_chased_at as SSOT proxy.
    const last = item.blocker.last_chased_at;
    if (last && (!entry.last_comm_iso || new Date(last) > new Date(entry.last_comm_iso))) {
      entry.last_comm_iso = last;
    }
  }

  const rows = Array.from(bucket.values()).map((e) => ({
    ...e,
    transactions_affected: e.deal_ids.size,
    days_since_last_comm: e.last_comm_iso
      ? Math.max(0, Math.floor((Date.now() - new Date(e.last_comm_iso)) / 86400000))
      : 999,
    assigned_to_me: currentUserId ? e.assigned_ids.has(currentUserId) : false,
  }));

  // Deterministic 5-tier sort.
  rows.sort((a, b) => {
    if (b.transactions_affected !== a.transactions_affected) return b.transactions_affected - a.transactions_affected;
    if (b.oldest_delay_days     !== a.oldest_delay_days)     return b.oldest_delay_days     - a.oldest_delay_days;
    const at = a.nearest_exchange_iso ? new Date(a.nearest_exchange_iso).getTime() : Infinity;
    const bt = b.nearest_exchange_iso ? new Date(b.nearest_exchange_iso).getTime() : Infinity;
    if (at !== bt) return at - bt;
    if (b.potential_fee         !== a.potential_fee)         return b.potential_fee         - a.potential_fee;
    return b.days_since_last_comm - a.days_since_last_comm;
  });

  // Momentum glyph = deterministic bucket.
  return rows.map((r, i) => ({
    ...r,
    momentum: i === 0 && r.oldest_delay_days >= 30 ? "hot"
      : r.transactions_affected >= 3 ? "rising"
      : r.oldest_delay_days >= 14 ? "falling"
      : r.days_since_last_comm > 20 ? "new"
      : "steady",
  }));
}


/* ═══ Rule-based Recommended Next Action ═══════════════════════
 * Chooses ONE recommendation per contact, transparent + testable.
 */
export function recommendAction(row) {
  const b = row?.primary_blocker;
  const role = row?.role || "";
  if (!b) return { verb: "Follow up", target: row.contact?.name, headline: "Follow up on outstanding items" };
  const roleLabel = OWNER_ROLE_LABEL[role] || role || "the contact";
  // iter-alignment · P0 · Command Centre Data Reconciliation
  // (2026-02-28) — prefer the canonical operator instruction
  // (`next_action`) when the detector provides one; the
  // stage-based `title` is used as a fallback only.
  const instruction = (b.next_action || b.title || b.label || b.description || "outstanding item").replace(/\.$/, "");
  // Verb chosen from role.
  // iter-alignment · QA sweep (2026-08-23) — canonical backend slugs
  // are `solicitor_buyer` / `solicitor_seller`, so an `endsWith`
  // check fell through to the generic "Contact" verb.  Match either
  // spelling so a solicitor always reads "Chase".
  const verb = role.includes("solicitor") ? "Chase"
    : role === "buyer" || role === "seller" ? "Call"
    : role === "managing_agent" ? "Request"
    : role === "mortgage_broker" ? "Call"
    : "Contact";
  return {
    verb,
    target: row.contact?.name || roleLabel,
    headline: `${verb} ${row.contact?.name || roleLabel} — ${instruction}`.slice(0, 120),
  };
}


/* ═══ Compact UI helpers ═══════════════════════════════════════ */
const initials = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((n) => n[0]).join("").toUpperCase() || "?";
const fmtFee   = (n) => n ? "£" + Math.round(n).toLocaleString() : "—";
const fmtDays  = (n) => n === 0 ? "Today" : n === 1 ? "1 day" : `${n} days`;


function ContactAvatar({ row }) {
  const size = "w-11 h-11";
  return (
    <div className={`${size} rounded-full bg-slate-100 text-slate-700 font-bold text-[12.5px] inline-flex items-center justify-center shrink-0`}>
      {initials(row.contact?.name)}
    </div>
  );
}


function MomentumGlyph({ momentum }) {
  const spec = MOMENTUM_GLYPH[momentum] || MOMENTUM_GLYPH.steady;
  const Icon = spec.icon;
  return (
    <div className={`w-8 h-8 rounded-full inline-flex items-center justify-center ${spec.className} shrink-0`}>
      <Icon size={14} strokeWidth={2.4} />
    </div>
  );
}


function ActionButtons({ row, onSelect, hasValidContact = true }) {
  const stop = (fn) => (e) => { e.stopPropagation(); fn?.(row); };
  // Primary CTA verb tracks the operator playbook — solicitors →
  // Chase · buyers/sellers → Call · managing agent → Request ·
  // fallback → Contact.  Reuses the shared `recommendAction`
  // resolver so the button verb never contradicts the briefing
  // panel headline.
  //
  // iter-alignment · P0 · Today's Priorities row polish (2026-03-01)
  // When no real participant has been resolved for the row, the
  // Call / Chase CTAs would previously "invent" a contact by opening
  // the briefing panel against a synthetic bucket.  The buttons are
  // now disabled + toned to slate-300 so they clearly signal "no-op"
  // while the row still opens the briefing (which surfaces the same
  // message inline).  Overflow menu stays available for keyboard
  // parity.
  const primaryVerb = recommendAction(row).verb;
  const disabledCls = "cursor-not-allowed opacity-50 pointer-events-none";
  return (
    <div className="flex items-center gap-1.5 shrink-0 justify-end">
      <button
        type="button"
        onClick={stop(() => onSelect?.(row))}
        disabled={!hasValidContact}
        aria-disabled={!hasValidContact}
        className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-slate-50 hover:bg-slate-100 text-slate-700 text-[11.5px] font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-1 ${hasValidContact ? "pointer-events-auto" : disabledCls}`}
        data-testid={`hic-row-${row.contact.id}-call`}
        aria-label="Call"
        title={hasValidContact ? "Call" : "No contact assigned"}
      >
        <Phone size={11} strokeWidth={2.4} />Call
      </button>
      <button
        type="button"
        onClick={stop(() => onSelect?.(row))}
        disabled={!hasValidContact}
        aria-disabled={!hasValidContact}
        className={`inline-flex items-center gap-1 h-7 px-3 rounded-md text-[11.5px] font-semibold cf-press transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-1 ${
          hasValidContact
            ? "pointer-events-auto bg-violet-600 hover:bg-violet-700 text-white"
            : `bg-slate-100 text-slate-400 ${disabledCls}`
        }`}
        data-testid={`hic-row-${row.contact.id}-chase`}
        aria-label={hasValidContact ? `${primaryVerb} — open briefing` : "No contact assigned"}
        title={hasValidContact ? `${primaryVerb} — open briefing` : "No contact assigned"}
      >
        {primaryVerb} <ChevronRight size={11} strokeWidth={2.6} />
      </button>
      <button
        type="button"
        onClick={stop(() => onSelect?.(row))}
        className="pointer-events-auto p-1.5 rounded-md hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-1"
        title="More actions"
        aria-label="More actions"
        data-testid={`hic-row-${row.contact.id}-more`}
      >
        <MoreHorizontal size={14} className="text-slate-400" />
      </button>
    </div>
  );
}


function Row({ row, selected, onSelect }) {
  const b = row.primary_blocker;
  const overdue = row.oldest_delay_days;
  /* iter-alignment · P67 · Today's Priorities row refresh (2026-02).
   *
   * Reads as Action → Who → Impact → Urgency → Actions.  The
   * required action is the strongest text on the row; the
   * spreadsheet-style four-column eyebrow layout is retired.
   * SSOT, ranking, filters, momentum + severity signals are
   * preserved verbatim. */

  // Action — canonical operator instruction (blocker.title first,
  // next_action fallback).  Preserves natural wording like
  // "Awaiting signed contract"; only strips a redundant leading
  // "Waiting for" when it precedes another action verb so copy
  // like "Waiting for awaiting signed contract" collapses to the
  // clean "Awaiting signed contract" form.
  const actionRaw = (b?.title || b?.next_action || "Response required").toString().trim();
  const action = actionRaw.replace(/^Waiting for\s+(?=(awaiting|waiting|pending|chasing)\b)/i, "");

  // Urgency lane — overdue items ALWAYS read as overdue.  Severity
  // escalates rose-700 (≥30d) → rose-600 (≥14d) → amber-700 (≥1d).
  // Zero-day blockers with a same-day / next-day exchange target
  // surface as `Due today` / `Due tomorrow`; anything else falls
  // back to the calm `Just raised` branch.
  const nearest = row.nearest_exchange_iso
    ? Math.floor((new Date(row.nearest_exchange_iso) - Date.now()) / 86_400_000)
    : null;
  let urgencyLabel;
  let urgencyTone;
  if (overdue >= 30) {
    urgencyLabel = `${fmtDays(overdue)} overdue`;
    urgencyTone = "text-rose-700";
  } else if (overdue >= 14) {
    urgencyLabel = `${fmtDays(overdue)} overdue`;
    urgencyTone = "text-rose-600";
  } else if (overdue >= 1) {
    urgencyLabel = `${fmtDays(overdue)} overdue`;
    urgencyTone = "text-amber-700";
  } else if (nearest === 0) {
    urgencyLabel = "Due today";
    urgencyTone = "text-amber-700";
  } else if (nearest === 1) {
    urgencyLabel = "Due tomorrow";
    urgencyTone = "text-amber-600";
  } else {
    urgencyLabel = "Just raised";
    urgencyTone = "text-slate-500";
  }

  // Who line — Contact · Role · Company on a single calm caption.
  // iter-alignment · P0 · Today's Priorities row polish (2026-03-01)
  // Synthetic role-scoped buckets no longer render "agent · agent · —"
  // style broken metadata; when no real participant resolves we render
  // a single calm "No contact assigned" caption instead.  Real contacts
  // dedupe name / role / company and drop the "—" placeholder company.
  const roleLabel = OWNER_ROLE_LABEL[row.role] || row.role;
  const hasValidContact =
    !!row.contact
    && row.contact._synthetic !== true
    && !String(row.contact.id || "").startsWith("role:")
    && !!row.contact.name
    && String(row.contact.name).toLowerCase() !== String(roleLabel || "").toLowerCase();
  const rawWhoParts = hasValidContact
    ? [row.contact.name, roleLabel, row.contact.company]
    : [];
  const seen = new Set();
  const whoParts = rawWhoParts
    .filter((p) => p && String(p).trim() && p !== "—")
    .filter((p) => {
      const k = String(p).toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  const whoLabel = whoParts.length ? whoParts.join(" · ") : null;

  // Impact — £ potential is now the primary signal (transaction count
  // de-emphasised as a small caption underneath).
  const txnCount = row.transactions_affected || 0;
  const feeLabel = row.potential_fee ? fmtFee(row.potential_fee) : null;

  return (
    <div
      className={`relative w-full grid grid-cols-[4px_minmax(0,1fr)_minmax(0,1fr)] lg:grid-cols-[4px_minmax(0,1fr)_140px_184px] xl:grid-cols-[4px_minmax(0,1.2fr)_140px_minmax(240px,1.2fr)_184px] items-center gap-x-4 gap-y-2.5 lg:gap-x-6 lg:gap-y-2 xl:gap-6 px-6 py-3.5 border-b border-slate-100/70 last:border-b-0 text-left cf-press transition-colors ${selected ? "bg-violet-50/30" : "hover:bg-slate-50"}`}
      data-testid={`hic-row-${row.contact.id}`}
      data-selected={selected ? "true" : "false"}
    >
      {/* Full-row open control (stretched button); actions sit above it as siblings. */}
      <button
        type="button"
        onClick={() => onSelect?.(row)}
        aria-label={`Open priority: ${action}${whoLabel ? ` — ${whoLabel}` : ""}`}
        aria-pressed={selected}
        className="absolute inset-0 w-full h-full cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-violet-500"
        data-testid={`hic-row-${row.contact.id}-open`}
      />
      <div className={`w-1 h-11 rounded-full ${row.momentum === "hot" ? "bg-rose-500" : row.momentum === "rising" ? "bg-emerald-500" : row.momentum === "falling" ? "bg-amber-400" : "bg-slate-200"}`} />
      {/* Avatar + text share one cell in normal flow: fixed avatar, 16px gap, wrapping text. */}
      <div className="col-start-2 col-span-2 lg:col-span-1 xl:col-start-auto flex items-center gap-4 min-w-0" data-testid={`hic-row-${row.contact.id}-identity`}>
        <div className="flex items-center gap-2 shrink-0">
          <MomentumGlyph momentum={row.momentum} />
          <ContactAvatar row={row} />
        </div>
        <div className="min-w-0 flex-1" data-testid={`hic-row-${row.contact.id}-action`}>
          <p
            className="text-[14px] font-bold text-slate-900 leading-tight break-words"
            title={action}
            data-testid={`hic-row-${row.contact.id}-action-title`}
          >
            {action}
          </p>
          {whoLabel ? (
            <p
              className="text-[11.5px] text-slate-500 break-words mt-0.5"
              data-testid={`hic-row-${row.contact.id}-who`}
              title={whoLabel}
            >
              {whoLabel}
            </p>
          ) : (
            <p
              className="text-[11.5px] text-slate-400 italic mt-0.5"
              data-testid={`hic-row-${row.contact.id}-who`}
            >
              No contact assigned
            </p>
          )}
        </div>
      </div>
      <div className="col-start-2 row-start-2 lg:col-start-3 lg:row-start-1 xl:col-start-auto xl:row-start-auto min-w-0 text-left" data-testid={`hic-row-${row.contact.id}-impact`}>
        {feeLabel ? (
          <p className="text-[13px] font-bold text-slate-900 tabular-nums leading-tight">
            {feeLabel}
          </p>
        ) : (
          <p className="text-[13px] font-bold text-slate-300 tabular-nums leading-tight">—</p>
        )}
        <p className="text-[10.5px] text-slate-400 mt-0.5 tabular-nums">
          {feeLabel ? "potential · " : ""}{txnCount} transaction{txnCount === 1 ? "" : "s"}
        </p>
      </div>
      <div className="col-start-3 row-start-2 lg:col-start-2 lg:col-span-3 xl:col-start-auto xl:col-span-1 xl:row-start-auto min-w-0 text-left" data-testid={`hic-row-${row.contact.id}-urgency`}>
        <p className={`text-[13.5px] font-bold ${urgencyTone} leading-tight`}>
          {urgencyLabel}
        </p>
        {b?.next_action && b.next_action !== actionRaw && (
          <p
            className="text-[11px] text-slate-500 truncate mt-0.5"
            title={b.next_action}
            data-testid={`hic-row-${row.contact.id}-reason`}
          >
            {b.next_action}
          </p>
        )}
      </div>
      <div className="relative z-10 pointer-events-none col-start-2 col-span-2 row-start-3 lg:col-start-4 lg:col-span-1 lg:row-start-1 xl:col-start-auto xl:row-start-auto flex justify-start lg:justify-end">
        <ActionButtons row={row} onSelect={onSelect} hasValidContact={hasValidContact} />
      </div>
    </div>
  );
}


function FilterChip({ label, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-[12px] font-semibold transition ${active ? "bg-violet-600 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-slate-300"}`}
      data-testid={testid}
    >
      {label}
    </button>
  );
}


function FilterPanelRow({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-2 py-2 border-b border-slate-100 last:border-b-0">
      <span className="text-[11.5px] text-slate-600 font-semibold">{label}</span>
      <div className="flex items-center gap-1.5">{children}</div>
    </div>
  );
}
function FilterChoice({ value, label, activeVal, onPick, testid }) {
  return (
    <button type="button" onClick={() => onPick(value)} className={`px-2 py-1 rounded-md text-[11px] font-semibold ${activeVal === value ? "bg-violet-600 text-white" : "bg-slate-50 text-slate-600 hover:bg-slate-100"}`} data-testid={testid}>{label}</button>
  );
}


function MoreFiltersPanel({ filters, onChange, onClose }) {
  const set = (k, v) => onChange({ ...filters, [k]: v });
  return (
    <div className="absolute right-0 top-full mt-2 z-30 w-[300px] rounded-xl bg-white ring-1 ring-slate-200 shadow-xl p-3" data-testid="hic-more-filters-panel">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] uppercase tracking-[0.08em] font-bold text-slate-500">More filters</p>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="hic-more-filters-close"><X size={14} /></button>
      </div>
      <FilterPanelRow label="Status">
        <FilterChoice value="any"      label="Any"    activeVal={filters.status} onPick={(v) => set("status", v)} testid="hic-filter-status-any" />
        <FilterChoice value="needs"    label="Needs"  activeVal={filters.status} onPick={(v) => set("status", v)} testid="hic-filter-status-needs" />
        <FilterChoice value="atrisk"   label="Risk"   activeVal={filters.status} onPick={(v) => set("status", v)} testid="hic-filter-status-atrisk" />
        <FilterChoice value="waiting"  label="Waiting" activeVal={filters.status} onPick={(v) => set("status", v)} testid="hic-filter-status-waiting" />
      </FilterPanelRow>
      <FilterPanelRow label="Transactions">
        <FilterChoice value={0}  label="Any" activeVal={filters.min_transactions} onPick={(v) => set("min_transactions", v)} testid="hic-filter-txn-any" />
        <FilterChoice value={2}  label="2+"  activeVal={filters.min_transactions} onPick={(v) => set("min_transactions", v)} testid="hic-filter-txn-2" />
        <FilterChoice value={3}  label="3+"  activeVal={filters.min_transactions} onPick={(v) => set("min_transactions", v)} testid="hic-filter-txn-3" />
      </FilterPanelRow>
      <FilterPanelRow label="Exchange target">
        <FilterChoice value={null} label="Any"  activeVal={filters.exchange_window} onPick={(v) => set("exchange_window", v)} testid="hic-filter-exch-any" />
        <FilterChoice value={7}    label="7d"   activeVal={filters.exchange_window} onPick={(v) => set("exchange_window", v)} testid="hic-filter-exch-7" />
        <FilterChoice value={14}   label="14d"  activeVal={filters.exchange_window} onPick={(v) => set("exchange_window", v)} testid="hic-filter-exch-14" />
        <FilterChoice value={30}   label="30d"  activeVal={filters.exchange_window} onPick={(v) => set("exchange_window", v)} testid="hic-filter-exch-30" />
      </FilterPanelRow>
      <FilterPanelRow label="Overdue only">
        <label className="inline-flex items-center gap-1.5">
          <input type="checkbox" checked={filters.overdue_only} onChange={(e) => set("overdue_only", e.target.checked)} data-testid="hic-filter-overdue" />
        </label>
      </FilterPanelRow>
      <FilterPanelRow label="Assigned to me">
        <label className="inline-flex items-center gap-1.5">
          <input type="checkbox" checked={filters.mine_only} onChange={(e) => set("mine_only", e.target.checked)} data-testid="hic-filter-mine" />
        </label>
      </FilterPanelRow>
    </div>
  );
}


/* ═══ Agent-drawer helpers ═════════════════════════════════════
 * iter-alignment · Agent Drawer Pilot Refinement (2026-02).
 * Groups the row's `items` (SSOT blocker envelope from
 * /api/today/sheet) by canonical `blocker.title` so repeated
 * blockers across transactions dedupe to one row.  Oldest
 * group ranks first; ties broken by transaction count. */
export function groupBlockerItems(items = []) {
  const by = new Map();
  for (const it of items) {
    if (!it || !it.blocker) continue;
    const title = String(it.blocker.title || it.blocker.next_action || "Outstanding item").trim();
    const age   = Number(it.blocker.age_days ?? it.blocker.days_open ?? 0) || 0;
    if (!by.has(title)) by.set(title, { title, deal_ids: new Set(), max_age: 0 });
    const g = by.get(title);
    g.deal_ids.add(it.deal_id);
    if (age > g.max_age) g.max_age = age;
  }
  return Array.from(by.values())
    .map((g) => ({ title: g.title, txn_count: g.deal_ids.size, max_age: g.max_age }))
    .sort((a, b) => b.max_age - a.max_age || b.txn_count - a.txn_count);
}

/* UK-readable short date ("12 Nov 2026") — reused for the
 * Exchange column of the Affected transactions list. */
function fmtUKShortDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function firstNameOf(fullName = "") {
  const t = String(fullName).trim().split(/\s+/)[0];
  return t || "contact";
}

/* Compact KPI tile used by the drawer's 3-up KPI strip. */
function BriefingKpi({ icon: Icon, label, value, tone }) {
  const toneMap = {
    violet:  { bg: "bg-violet-50",  fg: "text-violet-600"  },
    rose:    { bg: "bg-rose-50",    fg: "text-rose-600"    },
    emerald: { bg: "bg-emerald-50", fg: "text-emerald-600" },
  };
  const t = toneMap[tone] || toneMap.violet;
  return (
    <div className="rounded-xl bg-white ring-1 ring-slate-100 p-3">
      <div className={`w-7 h-7 rounded-lg inline-flex items-center justify-center ${t.bg} ${t.fg}`}>
        <Icon size={14} strokeWidth={2.4} />
      </div>
      <p className="mt-2 text-[18px] font-bold text-slate-900 tabular-nums leading-none">{Number(value) || 0}</p>
      <p className="mt-1 text-[10.5px] text-slate-500 leading-tight">{label}</p>
    </div>
  );
}

/* ═══ Right agent drawer (refined pilot) ═══════════════════════
 * Operational layout per approved reference image:
 *   1. Contact header (name · role · company · N active transactions)
 *   2. Quick actions (Call / Email / WhatsApp / Request update)
 *   3. WHAT NEEDS ATTENTION — blockers grouped & deduped
 *   4. KPI strip (Active transactions · Overdue actions · Exchanges this week)
 *   5. AFFECTED TRANSACTIONS — property · blocker · Waiting Xd · Exchange date
 *   6. RECOMMENDED NEXT ACTION — rule-based, derived from oldest actionable blocker
 *
 * Zero new endpoints, zero new state.  Every field derives from
 * the existing `row` aggregated upstream by `aggregateContacts`. */
function BriefingPanel({ row, deals, feeByDeal, tExByDeal, onClose }) {
  if (!row) return null;
  const groups        = groupBlockerItems(row.items);
  const totalActions  = groups.length;
  // iter-alignment · Agent Drawer Pilot Closure (2026-02) — the
  // blocker envelope does not carry a canonical due-date or
  // SLA-breach field (see /app/backend/blocker_engine.py).
  // `age_days` is purely "days since trigger event", so an SLA-
  // style breach count would be invented, not derived.  The
  // canonical signal is the detector itself: every item in
  // `row.items` is a currently-outstanding blocker.  The KPI
  // reads "Outstanding actions" and counts those SSOT items.
  const outstandingActions = row.items.length;
  const roleLabel     = OWNER_ROLE_LABEL[row.role]
    || (row.role
      ? String(row.role).split("_").map((w) => w ? w[0].toUpperCase() + w.slice(1) : "").join(" ").trim()
      : "");
  const synthetic     = row.contact?._synthetic === true;
  const resolvedName  = !synthetic && row.contact?.name && String(row.contact.name).toLowerCase() !== String(roleLabel).toLowerCase()
    ? row.contact.name
    : null;
  // Synthetic rows render a calm "No contact assigned" caption
  // rather than leaking a raw role slug ("agent", "broker") into
  // the header.  Real contacts always keep their canonical name.
  const displayName   = resolvedName || (synthetic ? "No contact assigned" : `Unassigned ${roleLabel || "contact"}`.trim());
  const companyLabel  = row.contact?.company && row.contact.company !== "—" ? row.contact.company : null;
  const roleCompany   = [roleLabel, companyLabel].filter(Boolean).join(" · ");
  const txnCount      = row.transactions_affected || 0;

  const affectedDeals = Array.from(row.deal_ids)
    .map((id) => ({
      deal: deals.find((d) => d.id === id),
      item: row.items.find((it) => it.deal_id === id),
    }))
    .filter((x) => x.deal);

  // Headline for the recommended action — join the top two
  // canonical blocker titles (trimmed of the trailing "outstanding"
  // / "required" wording so the sentence reads naturally).
  const recHeadlineParts = groups.slice(0, 2).map((g) =>
    g.title.replace(/\s+outstanding$/i, "").replace(/\s+required$/i, "").replace(/\s+pending$/i, ""),
  );
  const recHeadline = recHeadlineParts.length
    ? `Chase ${recHeadlineParts.join(" and ")}`
    : (recommendAction(row).headline || "Follow up on outstanding items");
  const oldestItem = groups[0];

  // Drawer actions reuse the shared contact links + the Deal Room Request Update deep link
  // (CommunicationDeepLink `?cc=1&action=chase`) for the oldest outstanding item.
  const phone = synthetic ? "" : (row.contact?.mobile || row.contact?.phone || "");
  const callHref = phone ? telHref(phone) : null;
  const waHref = phone ? whatsappHref(phone) : null;
  const mailHref = synthetic ? null : mailtoHref(row.contact?.email);
  const primaryItem = row.items.find((it) => it.blocker === row.primary_blocker) || row.items[0];
  const chaseHref = !synthetic && primaryItem?.deal_id && primaryItem?.blocker?.id
    ? `/deal-rooms/${primaryItem.deal_id}?cc=1&action=chase&blocker=${encodeURIComponent(primaryItem.blocker.id)}`
    : null;
  const qaCls = "flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-700 text-[12px] font-semibold";
  const qaOff = "flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg bg-slate-50 text-slate-300 text-[12px] font-semibold cursor-not-allowed";

  return (
    <aside
      className="fixed inset-y-0 right-0 z-50 w-full md:w-[420px] xl:top-[var(--cf-topbar-h,0px)] bg-white shadow-2xl border-l border-slate-200 flex flex-col"
      data-testid="hic-briefing-panel"
    >
      {/* 1 · Contact header */}
      <header className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <ContactAvatar row={row} />
          <div className="min-w-0">
            <p
              className="text-[15px] font-bold text-slate-900 truncate"
              data-testid="hic-briefing-name"
              title={displayName}
            >
              {displayName}
            </p>
            {roleCompany && (
              <p
                className="text-[12px] text-slate-500 truncate mt-0.5"
                data-testid="hic-briefing-role-company"
                title={roleCompany}
              >
                {roleCompany}
              </p>
            )}
            <p
              className="text-[11.5px] text-slate-400 mt-0.5 tabular-nums"
              data-testid="hic-briefing-active-count"
            >
              {txnCount} active transaction{txnCount === 1 ? "" : "s"}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-700 shrink-0"
          data-testid="hic-briefing-close"
          aria-label="Close agent drawer"
        >
          <X size={16} />
        </button>
      </header>

      {/* 2 · Quick actions — Call / Email / WhatsApp / Request update */}
      <div className="px-5 py-3 flex items-center gap-2 border-b border-slate-100">
        {callHref
          ? <a href={callHref} className={qaCls} data-testid="hic-briefing-call" title={`Call ${phone}`}><Phone size={12} strokeWidth={2.4} />Call</a>
          : <button type="button" disabled className={qaOff} data-testid="hic-briefing-call" title="No phone number saved"><Phone size={12} strokeWidth={2.4} />Call</button>}
        {mailHref
          ? <a href={mailHref} className={qaCls} data-testid="hic-briefing-email" title={`Email ${row.contact.email}`}><Mail size={12} strokeWidth={2.4} />Email</a>
          : <button type="button" disabled className={qaOff} data-testid="hic-briefing-email" title="No email address saved"><Mail size={12} strokeWidth={2.4} />Email</button>}
        {waHref
          ? <a href={waHref} target="_blank" rel="noopener noreferrer" className={qaCls} data-testid="hic-briefing-whatsapp" title={`WhatsApp ${phone}`}><MessageCircle size={12} strokeWidth={2.4} />WhatsApp</a>
          : <button type="button" disabled className={qaOff} data-testid="hic-briefing-whatsapp" title="No mobile number saved"><MessageCircle size={12} strokeWidth={2.4} />WhatsApp</button>}
        {chaseHref
          ? <Link to={chaseHref} onClick={() => onClose?.()} className={qaCls} data-testid="hic-briefing-request" title="Open the Request Update draft for the oldest outstanding item"><Send size={12} strokeWidth={2.4} />Request update</Link>
          : <button type="button" disabled className={qaOff} data-testid="hic-briefing-request" title="No contact assigned"><Send size={12} strokeWidth={2.4} />Request update</button>}
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        {/* 3 · WHAT NEEDS ATTENTION — deduped blocker groups.
             testid `hic-briefing-call-brief` retained for pilot
             contract parity (ex-"Call Brief" slot). */}
        <section
          data-testid="hic-briefing-call-brief"
          className="rounded-xl ring-1 ring-rose-100 bg-rose-50/40 p-3"
        >
          <p className="text-[10.5px] uppercase tracking-[0.08em] font-bold text-rose-700">
            What needs attention
          </p>
          <p className="text-[11.5px] text-slate-600 mt-0.5">
            {totalActions} outstanding action{totalActions === 1 ? "" : "s"} across {txnCount} transaction{txnCount === 1 ? "" : "s"}
          </p>
          {groups.length === 0 ? (
            <p className="mt-3 text-[12px] text-slate-500 italic" data-testid="hic-briefing-blocker-empty">
              Nothing outstanding — this contact is clear.
            </p>
          ) : (
            <ul className="mt-3 space-y-2" data-testid="hic-briefing-blocker-groups">
              {groups.slice(0, 4).map((g, i) => {
                const ageTone  = g.max_age >= 14 ? "text-rose-700" : g.max_age >= 7 ? "text-rose-600" : g.max_age >= 1 ? "text-amber-700" : "text-slate-500";
                const iconTone = g.max_age >= 14 ? "text-rose-500" : g.max_age >= 7 ? "text-rose-500" : g.max_age >= 1 ? "text-amber-500" : "text-slate-400";
                return (
                  <li
                    key={g.title}
                    className="flex items-start justify-between gap-3 bg-white rounded-lg ring-1 ring-slate-100 px-3 py-2"
                    data-testid={`hic-briefing-blocker-${i}`}
                  >
                    <div className="flex items-start gap-2 min-w-0">
                      <AlertCircle size={14} strokeWidth={2.4} className={`mt-0.5 shrink-0 ${iconTone}`} />
                      <div className="min-w-0">
                        <p className="text-[12.5px] font-semibold text-slate-900 truncate">{g.title}</p>
                        <p className="text-[11px] text-slate-500 tabular-nums mt-0.5">
                          {g.txn_count} transaction{g.txn_count === 1 ? "" : "s"}
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`text-[12.5px] font-bold tabular-nums ${ageTone}`}>
                        {fmtDays(g.max_age)}
                      </p>
                      <p className="text-[10.5px] text-slate-400">{i === 0 ? "oldest waiting" : "waiting"}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* 4 · KPI strip */}
        <section data-testid="hic-briefing-metrics" className="grid grid-cols-3 gap-2">
          <BriefingKpi icon={Building2}      label="Active transactions"  value={txnCount}            tone="violet"  />
          <BriefingKpi icon={Clock}          label="Outstanding actions"  value={outstandingActions}  tone="rose"    />
          <BriefingKpi icon={ArrowRightLeft} label="Exchanges this week"  value={row.exchanges_this_week} tone="emerald" />
        </section>

        {/* 5 · Affected transactions */}
        <section data-testid="hic-briefing-affected">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500">
              Affected transactions
            </p>
            {affectedDeals.length > 5 && (
              <Link
                to="/sales-progression?filter=needs-attention&sort=priority"
                className="text-[11px] font-semibold text-violet-700 hover:text-violet-800 inline-flex items-center gap-0.5"
                data-testid="hic-briefing-affected-view-all"
              >
                View all {affectedDeals.length}
                <ChevronRight size={10} strokeWidth={2.6} />
              </Link>
            )}
          </div>
          <ul className="space-y-1.5">
            {affectedDeals.slice(0, 5).map(({ deal, item }) => {
              const title   = item?.blocker.title || "Outstanding item";
              const age     = Number(item?.blocker.age_days ?? item?.blocker.days_open ?? 0) || 0;
              const tex     = tExByDeal?.[deal.id];
              const exLabel = fmtUKShortDate(tex);
              return (
                <li key={deal.id} className="rounded-lg ring-1 ring-slate-100 hover:bg-slate-50 transition-colors">
                  <Link
                    to={`/deal-rooms/${deal.id}`}
                    className="flex items-center justify-between gap-2 px-3 py-2"
                    data-testid={`hic-briefing-affected-${deal.id}`}
                    onClick={() => onClose?.()}
                  >
                    <div className="min-w-0 flex items-start gap-2">
                      <Building2 size={14} className="text-slate-400 mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-[12.5px] font-semibold text-slate-900 truncate">
                          {deal.propertyAddress || deal.name || deal.id.slice(0, 8)}
                        </p>
                        <p className="text-[11px] truncate mt-0.5">
                          <span className="text-slate-600">{title}</span>
                          <span className="text-rose-600 font-semibold"> · Waiting {fmtDays(age)}</span>
                        </p>
                      </div>
                    </div>
                    {exLabel && (
                      <div className="text-right shrink-0">
                        <p className="text-[10px] text-slate-400 leading-none">Exchange</p>
                        <p className="text-[11.5px] font-semibold text-slate-700 tabular-nums mt-1">{exLabel}</p>
                      </div>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>

        {/* 6 · Recommended next action */}
        <section
          data-testid="hic-briefing-recommendation"
          className="rounded-xl bg-violet-50/70 ring-1 ring-violet-100 p-3"
        >
          <p className="text-[10.5px] uppercase tracking-[0.08em] font-bold text-violet-700 inline-flex items-center gap-1.5">
            <Sparkles size={11} strokeWidth={2.4} />
            Recommended next action
          </p>
          <p className="mt-1.5 text-[13px] font-bold text-slate-900">{recHeadline}</p>
          <p className="mt-1 text-[11.5px] text-slate-600">
            {txnCount} transaction{txnCount === 1 ? " is" : "s are"} currently waiting on this {(roleLabel || "contact").toLowerCase()}.
          </p>
          {oldestItem && (
            <p className="text-[11.5px] text-slate-600">
              Oldest outstanding item: {fmtDays(oldestItem.max_age)}.
            </p>
          )}
          <div className="mt-3 flex items-center justify-between gap-2">
            <p className="text-[10.5px] text-slate-500">
              Rule-based · derived from oldest actionable blocker
            </p>
            {resolvedName && chaseHref ? (
              <Link
                to={chaseHref}
                onClick={() => onClose?.()}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-[12px] font-bold cf-press"
                data-testid="hic-briefing-primary-cta"
              >
                Contact {firstNameOf(displayName)}
                <ChevronRight size={12} strokeWidth={2.6} />
              </Link>
            ) : affectedDeals[0]?.deal ? (
              <Link
                to={`/deal-rooms/${affectedDeals[0].deal.id}`}
                onClick={() => onClose?.()}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-[12px] font-bold cf-press"
                data-testid="hic-briefing-primary-cta"
              >
                View {txnCount} transaction{txnCount === 1 ? "" : "s"}
                <ChevronRight size={12} strokeWidth={2.6} />
              </Link>
            ) : (
              <button
                type="button"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-200 text-slate-500 text-[12px] font-bold cursor-not-allowed"
                data-testid="hic-briefing-primary-cta"
                disabled
              >
                No contact
                <ChevronRight size={12} strokeWidth={2.6} />
              </button>
            )}
          </div>
        </section>
      </div>
    </aside>
  );
}


/* ═══ Main workspace component ═════════════════════════════════ */
export default function HighestImpactConversations({
  items, deals, contacts, feeByDeal, tExByDeal, currentUserId, onEmpty,
}) {
  const [activeTab, setActiveTab] = useState("all");
  const [moreOpen, setMoreOpen]   = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [filters, setFilters] = useState({
    status: "any",
    min_transactions: 0,
    exchange_window: null,
    overdue_only: false,
    mine_only: false,
  });

  const allRows = useMemo(
    () => aggregateContacts({ items, deals, contacts, feeByDeal, tExByDeal, currentUserId }),
    [items, deals, contacts, feeByDeal, tExByDeal, currentUserId],
  );

  const rows = useMemo(() => {
    const tab = ROLE_TABS.find((t) => t.id === activeTab);
    let out = allRows;
    if (tab?.roles) out = out.filter((r) => tab.roles.includes(r.role));
    if (filters.min_transactions > 0) out = out.filter((r) => r.transactions_affected >= filters.min_transactions);
    if (filters.exchange_window) out = out.filter((r) => {
      if (!r.nearest_exchange_iso) return false;
      const days = Math.floor((new Date(r.nearest_exchange_iso) - Date.now()) / 86400000);
      return days >= 0 && days <= filters.exchange_window;
    });
    if (filters.overdue_only) out = out.filter((r) => r.oldest_delay_days > 0);
    if (filters.mine_only)    out = out.filter((r) => r.assigned_to_me);
    if (filters.status === "needs")   out = out.filter((r) => r.oldest_delay_days >= 7);
    if (filters.status === "atrisk")  out = out.filter((r) => r.oldest_delay_days >= 14);
    if (filters.status === "waiting") out = out.filter((r) => r.oldest_delay_days < 7);
    return out;
  }, [allRows, activeTab, filters]);

  const selected = selectedId ? allRows.find((r) => r.contact.id === selectedId) : null;

  // Only fall back to the legacy queue when there are literally
  // no items to rank — the presence of items but zero resolvable
  // contacts is now surfaced via synthetic role buckets.
  if (!items || items.length === 0) return onEmpty?.() || null;

  return (
    <section className="mt-8" data-testid="hic-section">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <p className="text-[10.5px] uppercase tracking-[0.08em] font-bold text-slate-500">Today&apos;s Priorities</p>
          <p className="text-[12px] text-slate-500">Ranked by conversation momentum.</p>
        </div>
      </div>

      {/* Tabs + sort + more */}
      <div className="flex items-center justify-between gap-3 mb-3 relative">
        <div className="flex flex-wrap items-center gap-1.5">
          {ROLE_TABS.map((t) => {
            // iter-alignment · P0 · Command Centre Data Reconciliation
            // (2026-02-28) — Tab counts now reflect the number of
            // TRANSACTIONS in scope, not the aggregated contact
            // buckets.  Previous "All 1" masked the fact that a
            // single solicitor was blocking 7 deals; "All 7" tells
            // the operator the true operational load.
            const scopeRows = t.roles ? allRows.filter((r) => t.roles.includes(r.role)) : allRows;
            const count = scopeRows.reduce((n, r) => n + (r.transactions_affected || 0), 0);
            return (
              <FilterChip
                key={t.id}
                label={t.id === "all" ? `All ${count}` : `${t.label} ${count}`}
                active={activeTab === t.id}
                onClick={() => setActiveTab(t.id)}
                testid={`hic-tab-${t.id}`}
              />
            );
          })}
          <button
            type="button"
            onClick={() => setMoreOpen((v) => !v)}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-semibold bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-slate-300"
            data-testid="hic-more-filters-toggle"
          >
            <Plus size={11} strokeWidth={2.4} />More filters
          </button>
          {moreOpen && (
            <MoreFiltersPanel
              filters={filters}
              onChange={setFilters}
              onClose={() => setMoreOpen(false)}
            />
          )}
        </div>
        <div className="text-[11.5px] text-slate-500 inline-flex items-center gap-1 shrink-0">
          Sort by <span className="font-semibold text-slate-700">Momentum</span> <ChevronDown size={12} />
        </div>
      </div>

      {/* Row list — sorting is already communicated by the subtitle
          "Ranked by conversation momentum." and the top-right sort
          control, so no redundant footer sits inside the card. */}
      <div className="rounded-2xl bg-white overflow-hidden" style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 0 0 1px rgba(226,232,240,0.7)" }}>
        {rows.length === 0 ? (
          <div className="py-8 text-center text-[12.5px] text-slate-500" data-testid="hic-empty">No conversations match these filters.</div>
        ) : rows.slice(0, 12).map((row) => (
          <Row
            key={row.contact.id}
            row={row}
            selected={selectedId === row.contact.id}
            onSelect={(r) => setSelectedId(r.contact.id)}
          />
        ))}
      </div>

      {selected && (
        <BriefingPanel
          row={selected}
          deals={deals}
          feeByDeal={feeByDeal}
          tExByDeal={tExByDeal}
          onClose={() => setSelectedId(null)}
        />
      )}
    </section>
  );
}
